import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  getInitialTheme, 
  applyTheme, 
  getThemeConfig,
  getThemeColors,
  getBaseColors,
  getGradients,
  generateCSSVariables,
  getStoredTheme
} from '../config/appConfig';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children, forceTheme }) => {
  const [theme, setTheme] = useState(forceTheme || getInitialTheme());
  const [isLoading, setIsLoading] = useState(true);
  const config = getThemeConfig();

  const activeTheme = forceTheme || theme;

  useEffect(() => {
    applyTheme(activeTheme);
    
    if (activeTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    setIsLoading(false);
  }, [activeTheme]);

  useEffect(() => {
    if (forceTheme || !config.systemThemeDetection) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!getStoredTheme()) {
        const newTheme = e.matches ? 'dark' : 'light';
        setTheme(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [config.systemThemeDetection, forceTheme]);

  useEffect(() => {
    if (forceTheme) {
        setTheme(forceTheme)
    } else {
        const initialTheme = getInitialTheme();
        setTheme(initialTheme);
    }
  }, [forceTheme]);

  const toggleTheme = () => {
    if (forceTheme) return;
    setTheme(current => {
      const newTheme = current === 'light' ? 'dark' : 'light';
      applyTheme(newTheme);
      return newTheme;
    });
  };

  const setThemeManually = (newTheme) => {
    if (forceTheme) return;
    if (['light', 'dark'].includes(newTheme)) {
      setTheme(newTheme);
      applyTheme(newTheme);
    }
  };

  const currentThemeColors = getThemeColors(activeTheme);
  const baseColors = getBaseColors();
  const gradients = getGradients(activeTheme);
  const cssVariables = generateCSSVariables(activeTheme);

  const value = {
    theme: activeTheme,
    isLoading,
    isDark: activeTheme === 'dark',
    isLight: activeTheme === 'light',
    setTheme: setThemeManually,
    toggleTheme,
    config,
    colors: currentThemeColors,
    baseColors,
    gradients,
    cssVariables,
    getThemeColors: (themeName) => getThemeColors(themeName),
    generateCSSVariables: (themeName) => generateCSSVariables(themeName),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const withTheme = (Component) => {
  return function ThemedComponent(props) {
    const theme = useTheme();
    return <Component {...props} theme={theme} />;
  };
};

export const useThemeStyles = () => {
  const { colors, baseColors, gradients, cssVariables } = useTheme();
  
  return {
    colors,
    baseColors,
    gradients,
    cssVariables,
    
    cardStyle: {
      backgroundColor: colors.cardBackground,
      border: `1px solid ${colors.cardBorder}`,
      borderRadius: '8px',
    },
    
    inputStyle: {
      backgroundColor: colors.inputBackground,
      border: `1px solid ${colors.inputBorder}`,
      color: colors.text,
    },
    
    textStyles: {
      primary: { color: colors.text },
      secondary: { color: colors.textSecondary },
      tertiary: { color: colors.textTertiary },
      inverse: { color: colors.textInverse },
    },
  };
};

export default ThemeProvider;